import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FictionalVehicle, fmt } from "@/hooks/useVehicleSim";
import { ArrowLeft, ArrowRight, Handshake, X } from "lucide-react";

interface Props {
  vehicle: FictionalVehicle;
  currentDiscount: number;
  onNegotiate: () => number;
  onContinue: () => void;
  onBack: () => void;
  onChooseAnother: () => void;
}

export default function NegotiationScreen({ vehicle, currentDiscount, onNegotiate, onContinue, onBack, onChooseAnother }: Props) {
  const [attempted, setAttempted] = useState(currentDiscount > 0);
  const [result, setResult] = useState<number>(currentDiscount);

  const handleNegotiate = () => {
    const discount = onNegotiate();
    setResult(discount);
    setAttempted(true);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Negotiation</h2>
        <p className="text-sm text-muted-foreground">Try to negotiate a better deal. Results aren't guaranteed.</p>
      </div>

      <div className="glass-card glow-border p-6 space-y-4 text-center">
        <Handshake className="w-12 h-12 text-accent mx-auto" />

        {!attempted ? (
          <>
            <p className="text-sm text-muted-foreground">
              The listed price is {fmt(vehicle.purchase_price)}. You can try to negotiate,
              continue at the listed price, or go back and choose another vehicle.
            </p>
            <Button onClick={handleNegotiate} className="w-full gap-2">
              <Handshake className="w-4 h-4" /> Attempt Negotiation
            </Button>
          </>
        ) : result > 0 ? (
          <>
            <div className="bg-emerald-500/10 text-emerald-600 p-4 rounded-xl">
              <p className="font-heading font-bold text-lg">Negotiation Successful!</p>
              <p className="text-sm mt-1">You saved {fmt(result)} off the listed price.</p>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Original Price</span><span className="line-through text-muted-foreground">{fmt(vehicle.purchase_price)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span className="text-emerald-600 font-semibold">−{fmt(result)}</span></div>
              <div className="flex justify-between font-semibold"><span className="text-foreground">New Price</span><span className="text-primary">{fmt(vehicle.purchase_price - result)}</span></div>
            </div>
          </>
        ) : (
          <div className="bg-muted p-4 rounded-xl">
            <p className="font-heading font-bold text-foreground">No Luck This Time</p>
            <p className="text-sm text-muted-foreground mt-1">The dealer wouldn't budge on the price. You'll continue at {fmt(vehicle.purchase_price)}.</p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Button onClick={onContinue} className="w-full gap-2">Continue to Final Summary <ArrowRight className="w-4 h-4" /></Button>
        <Button variant="outline" onClick={onChooseAnother} className="w-full">Choose Another Vehicle</Button>
        <Button variant="outline" onClick={onBack} className="w-full gap-2"><ArrowLeft className="w-4 h-4" /> Go Back</Button>
      </div>
    </div>
  );
}
